import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Headset, Truck, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { role: 'company_admin', label: 'Company Admin', icon: Shield },
  { role: 'dispatcher', label: 'Dispatcher', icon: Headset },
  { role: 'driver', label: 'Driver', icon: Truck },
];

// DEMO-ONLY. Rendered exclusively for users flagged `apple_review_persona`; the
// backend re-validates that the caller's company is the isolated review tenant.
export const ReviewRoleSwitcher = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  if (!user?.apple_review_persona) return null;

  const switchTo = async (role) => {
    if (role === user.role || busy) return;
    setBusy(role);
    setError('');
    try {
      const { data } = await axios.post(`${API}/review/switch-role`, { role }, { withCredentials: true });
      setUser(data);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not switch role');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="review-role-switcher"
      className="bg-slate-900 text-white px-3 py-2 border-b border-slate-800"
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">App Review demo</span>
        <span className="text-xs text-slate-300 hidden sm:inline">Sample data only · view as:</span>
        <div className="flex items-center gap-1 ml-auto sm:ml-0">
          {ROLES.map(({ role, label, icon: Icon }) => {
            const active = role === user.role;
            return (
              <button
                key={role}
                type="button"
                data-testid={`review-switch-${role}`}
                onClick={() => switchTo(role)}
                disabled={busy !== null}
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active ? 'bg-amber-400 text-slate-900' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                } disabled:opacity-60`}
              >
                {busy === role ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
                {label}
              </button>
            );
          })}
        </div>
        {error && <span data-testid="review-switch-error" className="text-xs text-red-300 w-full">{error}</span>}
      </div>
    </div>
  );
};

export default ReviewRoleSwitcher;
